import { Platform } from "react-native";

/**
 * Raw brand colours — theme-invariant. Use only where a colour is genuinely
 * fixed (the wordmark, an illustration). Everything else reads the semantic
 * {@link ThemeColors} set via `useTheme()` so it follows light / dark.
 */
export const palette = {
  brandSteel: "#1C2321",
  brandPaper: "#F4F1EA",
  brandAmber: "#E8A838",
  brandBlueprint: "#3D5A80",
  brandIron: "#C9C2B6",
} as const;

export const spacing = {
  xs: 4,
  s: 8,
  m: 12,
  l: 16,
  xl: 24,
  xxl: 32,
} as const;

export const radii = {
  xs: 2,
  s: 6,
  m: 10,
  l: 12,
  xl: 999,
  control: 10,
  card: 12,
  pill: 999,
} as const;

export const fonts = {
  regular: "IBMPlexSans_400Regular",
  medium: "IBMPlexSans_500Medium",
  semibold: "IBMPlexSans_600SemiBold",
  bold: "IBMPlexSans_700Bold",
  mono: "IBMPlexMono_400Regular",
  monoBold: "IBMPlexMono_700Bold",
} as const;

/**
 * Soft, warm-tinted elevation. On web these become real box shadows; on native
 * the shadow* props apply (iOS) alongside `elevation` (Android).
 */
type Elevation = {
  boxShadow: string;
  shadowColor: string;
  shadowOffset: { width: number; height: number };
  shadowOpacity: number;
  shadowRadius: number;
  elevation: number;
};

function elevation(
  offsetY: number,
  radius: number,
  opacity: number,
  android: number,
): Elevation {
  return {
    boxShadow: `0px ${offsetY}px ${radius}px rgba(28, 35, 33, ${opacity})`,
    shadowColor: palette.brandSteel,
    shadowOffset: { width: 0, height: offsetY },
    shadowOpacity: opacity,
    shadowRadius: radius,
    elevation: android,
  };
}

export const elevations = {
  sm: elevation(1, 3, 0.08, 1),
  md: elevation(6, 16, 0.1, 3),
  lg: elevation(16, 34, 0.14, 8),
} as const;

/** Pick the shadow shape the current platform actually understands. */
export function shadow(level: keyof typeof elevations) {
  const e = elevations[level];
  return Platform.OS === "web"
    ? { boxShadow: e.boxShadow }
    : {
        shadowColor: e.shadowColor,
        shadowOffset: e.shadowOffset,
        shadowOpacity: e.shadowOpacity,
        shadowRadius: e.shadowRadius,
        elevation: e.elevation,
      };
}

/**
 * Type ramp. `AppText` still exposes the xs/sm/base/lg sizes it always has;
 * this is the reference the composed components (PageTitle, SectionHeader,
 * MetricCard…) reach for when they need a real heading or a caption.
 */
export const type = {
  display: {
    fontFamily: fonts.bold,
    fontSize: 30,
    lineHeight: 34,
    letterSpacing: -0.4,
  },
  title: {
    fontFamily: fonts.bold,
    fontSize: 24,
    lineHeight: 29,
    letterSpacing: -0.3,
  },
  heading: {
    fontFamily: fonts.semibold,
    fontSize: 18,
    lineHeight: 24,
    letterSpacing: -0.2,
  },
  subheading: { fontFamily: fonts.semibold, fontSize: 15, lineHeight: 21 },
  body: { fontFamily: fonts.regular, fontSize: 15, lineHeight: 22 },
  bodyStrong: { fontFamily: fonts.medium, fontSize: 15, lineHeight: 22 },
  label: { fontFamily: fonts.medium, fontSize: 13, lineHeight: 17 },
  caption: { fontFamily: fonts.regular, fontSize: 12, lineHeight: 16 },
  overline: {
    fontFamily: fonts.monoBold,
    fontSize: 11,
    lineHeight: 14,
    letterSpacing: 0.8,
  },
  mono: { fontFamily: fonts.mono, fontSize: 13, lineHeight: 18 },
} as const;

export type ColorScheme = "light" | "dark";

/**
 * Semantic colour contract. The key names are shared across the app fleet;
 * `steel*` / `onSteel*` are GearSync's dark-anchor extension (the marketing
 * CTA/footer echo used on auth and upgrade surfaces).
 */
export type ThemeColors = {
  background: string;
  foreground: string;
  heading: string;
  elevated: string;
  surface: string;
  surfaceSunken: string;
  mutedSurface: string;
  mutedForeground: string;
  primary: string;
  primaryHover: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  accent: string;
  accentSurface: string;
  border: string;
  borderStrong: string;
  focusRing: string;
  success: string;
  successSurface: string;
  warning: string;
  danger: string;
  dangerSurface: string;
  overlay: string;
  steel: string;
  steelRaised: string;
  onSteel: string;
  onSteelMuted: string;
};

export const lightColors: ThemeColors = {
  background: "#F4F1EA",
  foreground: "#1C2321",
  heading: "#1C2321",
  elevated: "#FFFFFF",
  surface: "#EBE6DC",
  surfaceSunken: "#E4DECF",
  mutedSurface: "#E2DCCF",
  mutedForeground: "#5C635F",
  primary: "#3D5A80",
  primaryHover: "#324A6A",
  primaryForeground: "#FFFFFF",
  secondary: "#C9C2B6",
  secondaryForeground: "#1C2321",
  accent: "#E8A838",
  accentSurface: "#F7E7C6",
  border: "#D3CBBB",
  borderStrong: "#B7AD98",
  focusRing: "rgba(61, 90, 128, 0.45)",
  success: "#3D5A80",
  successSurface: "#E6ECF3",
  warning: "#E8A838",
  danger: "#b42318",
  dangerSurface: "#fdf0ee",
  overlay: "rgba(28, 35, 33, 0.45)",
  steel: "#1C2321",
  steelRaised: "#272E2B",
  onSteel: "#F4F1EA",
  onSteelMuted: "rgba(244, 241, 234, 0.68)",
};

/**
 * Dark palette — derived from the light "steel / paper / blueprint / amber"
 * set (3 Sep 2026, pending Brad's review in the running app): deep steel
 * grounds, paper-toned text, blueprint blue lifted for contrast, amber held.
 */
export const darkColors: ThemeColors = {
  background: "#181C1B",
  foreground: "#E8E4DA",
  heading: "#F4F1EA",
  elevated: "#222725",
  surface: "#1F2422",
  surfaceSunken: "#191E1C",
  mutedSurface: "#252A28",
  mutedForeground: "#9AA09A",
  primary: "#6E93C0",
  primaryHover: "#5E82AE",
  primaryForeground: "#12171B",
  secondary: "#3A403D",
  secondaryForeground: "#E8E4DA",
  accent: "#E8A838",
  accentSurface: "#3A2F16",
  border: "#333935",
  borderStrong: "#474D48",
  focusRing: "rgba(110, 147, 192, 0.5)",
  success: "#6E93C0",
  successSurface: "#20303E",
  warning: "#E8A838",
  danger: "#F0736A",
  dangerSurface: "#3A1F1D",
  overlay: "rgba(0, 0, 0, 0.55)",
  steel: "#0F1312",
  steelRaised: "#1A1F1D",
  onSteel: "#F4F1EA",
  onSteelMuted: "rgba(244, 241, 234, 0.68)",
};

export const tokens = {
  palette,
  spacing,
  radii,
  fonts,
  elevations,
  type,
  lightColors,
  darkColors,
} as const;
